<template>
  <div
    class="push-inbox-obscurator fade-obscurator"
    v-show="visible"
    @click.self="close"
  >
    <aside
      class="push-inbox-sidebar slide-left"
      :class="{ 'push-inbox-sidebar--mobile': isMobile }"
      role="dialog"
      aria-label="Notificações"
      @click.stop
    >
      <div class="push-inbox-header d-flex align-items-center">
        <i class="fa fa-bell text-blue mr-2" aria-hidden="true"></i>
        <h4 class="mb-0 push-inbox-title text-truncate">Notificações</h4>
        <button
          type="button"
          class="push-inbox-close ml-auto"
          title="Fechar"
          aria-label="Fechar"
          @click="close"
        >
          <i class="no no-e-remove text-blue" aria-hidden="true"></i>
        </button>
      </div>

      <div v-if="loading" class="push-inbox-empty text-muted">
        Carregando…
      </div>
      <div v-else-if="!items.length" class="push-inbox-empty text-muted">
        Nenhuma notificação push.
      </div>

      <ul v-else class="push-inbox-list list-unstyled mb-0">
        <li
          v-for="item in items"
          :key="item.id"
          class="push-inbox-card"
          :class="{
            'push-inbox-card--read': item.read,
            'push-inbox-card--open': expandedId === item.id,
          }"
          role="button"
          tabindex="0"
          :aria-expanded="expandedId === item.id ? 'true' : 'false'"
          @click="toggleExpand(item)"
          @keydown.enter.prevent="toggleExpand(item)"
          @keydown.space.prevent="toggleExpand(item)"
        >
          <div class="push-inbox-card-top d-flex align-items-start min-width-0">
            <div class="push-inbox-card-heading flex-grow-1 min-width-0">
              <div class="push-inbox-card-title text-truncate" :title="cardTitle(item)">
                {{ cardTitle(item) }}
              </div>
              <div class="push-inbox-card-meta text-muted text-truncate">
                {{ formatSendDate(item.send_date) }}
              </div>
            </div>
          </div>

          <div
            v-show="expandedId === item.id"
            class="push-inbox-card-body"
            @click.stop
          >
            <div v-if="modelMessageFields(item).length" class="push-inbox-fields">
              <div
                v-for="field in modelMessageFields(item)"
                :key="field.name"
                class="push-inbox-field"
              >
                <span class="push-inbox-field-name">{{ field.name }}:</span>
                {{ field.value }}
              </div>
            </div>
            <div v-else class="push-inbox-fields text-muted">
              <em>Sem conteúdo</em>
            </div>
            <div class="d-flex justify-content-end align-items-center mt-2">
              <button
                v-if="!item.read"
                type="button"
                class="push-inbox-icon-btn"
                title="Marcar como lida"
                aria-label="Marcar como lida"
                :disabled="markingId === item.id"
                @click.stop="markRead(item)"
              >
                <i class="fa fa-envelope-open" aria-hidden="true"></i>
              </button>
              <button
                v-else
                type="button"
                class="push-inbox-icon-btn"
                title="Marcar como não lida"
                aria-label="Marcar como não lida"
                :disabled="markingId === item.id"
                @click.stop="markUnread(item)"
              >
                <i class="fa fa-envelope" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </li>
      </ul>
    </aside>
  </div>
</template>

<script>
import nodeService from '@/services/nodejs'
import { extractModelMessageFields } from './utils/pushInboxWs'
import { isMobile } from '@/store/utils/general'

export default {
  name: 'PushInboxPanel',
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      visible: false,
      expandedId: null,
      markingId: null,
      isMobile: isMobile(),
    }
  },
  watch: {
    open(val) {
      if (val) {
        this.openPanel()
      } else {
        this.visible = false
        this.expandedId = null
        document.body.style.overflowY = 'auto'
      }
    },
  },
  methods: {
    openPanel() {
      document.body.style.overflowY = 'hidden'
      this.visible = true
      this.expandedId = null
    },
    close() {
      this.visible = false
      this.expandedId = null
      document.body.style.overflowY = 'auto'
      this.$emit('close')
    },
    cardTitle(item) {
      return (item && (item.title || item.model_name)) || 'Notificação'
    },
    toggleExpand(item) {
      this.expandedId = this.expandedId === item.id ? null : item.id
    },
    modelMessageFields(item) {
      return extractModelMessageFields(item && item.message)
    },
    formatSendDate(value) {
      if (!value) return ''
      const raw = String(value).replace(' ', 'T')
      const d = new Date(raw.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(raw) ? raw : raw + 'Z')
      if (Number.isNaN(d.getTime())) return String(value)
      return d.toLocaleString('pt-BR')
    },
    async markRead(item) {
      if (!item || item.read || this.markingId) return
      this.markingId = item.id
      try {
        const { data } = await nodeService.patchMessengerPushInboxRead(item.id)
        this.$emit('marked-read', {
          id: item.id,
          read_at: data && data.read_at,
          unread_count: data && data.unread_count,
        })
      } catch (err) {
        console.error('push inbox mark read', err)
        this.$notify &&
          this.$notify({
            type: 'danger',
            message: 'Não foi possível marcar como lida.',
          })
      } finally {
        this.markingId = null
      }
    },
    async markUnread(item) {
      if (!item || !item.read || this.markingId) return
      this.markingId = item.id
      try {
        const { data } = await nodeService.patchMessengerPushInboxUnread(item.id)
        this.$emit('marked-unread', {
          id: item.id,
          unread_count: data && data.unread_count,
        })
      } catch (err) {
        console.error('push inbox mark unread', err)
        this.$notify &&
          this.$notify({
            type: 'danger',
            message: 'Não foi possível marcar como não lida.',
          })
      } finally {
        this.markingId = null
      }
    },
  },
  beforeDestroy() {
    document.body.style.overflowY = 'auto'
  },
}
</script>

<style scoped>
.push-inbox-obscurator {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100vh;
  z-index: 1050;
  background: rgba(0, 0, 0, 0.5);
}

.push-inbox-sidebar {
  width: 400px;
  max-width: 100%;
  height: 100%;
  background: #fff;
  padding: 1rem 1rem 1.5rem;
  overflow-x: hidden;
  overflow-y: auto;
  position: absolute;
  right: 0;
  top: 0;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.12);
}

.push-inbox-sidebar--mobile {
  width: 100%;
}

.push-inbox-header {
  min-width: 0;
}

.push-inbox-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 1.2rem;
  color: #12576e;
}

.push-inbox-close {
  border: 0;
  background: transparent;
  padding: 0.25rem;
  cursor: pointer;
  line-height: 1;
  flex: 0 0 auto;
}

.push-inbox-empty {
  padding: 1.5rem 0.5rem;
  font-size: 0.95rem;
}

.push-inbox-list {
  margin-top: 0.75rem;
  overflow-x: hidden;
}

.push-inbox-card {
  border: 1px solid #e4e7ea;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.65rem;
  background: #fff;
  transition: opacity 0.2s ease, background 0.2s ease;
  cursor: pointer;
  overflow: hidden;
  max-width: 100%;
}

.push-inbox-card--read {
  opacity: 0.55;
  background: #f7f8f9;
}

.push-inbox-card-heading {
  min-width: 0;
  overflow: hidden;
}

.push-inbox-card-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #12576e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.push-inbox-card-meta {
  font-size: 0.75rem;
  margin-top: 0.15rem;
}

.push-inbox-card-body {
  margin-top: 0.65rem;
  padding-top: 0.65rem;
  border-top: 1px solid #eef0f2;
  cursor: default;
}

.push-inbox-fields {
  font-size: 0.9rem;
  color: #333;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.push-inbox-field {
  margin-bottom: 0.35rem;
}

.push-inbox-field:last-child {
  margin-bottom: 0;
}

.push-inbox-field-name {
  font-weight: 600;
  color: #12576e;
  margin-right: 0.25rem;
}

.push-inbox-icon-btn {
  border: 0;
  background: transparent;
  color: #12576e;
  padding: 0.15rem 0.35rem;
  cursor: pointer;
  line-height: 1;
}

.push-inbox-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.min-width-0 {
  min-width: 0;
}

.slide-left {
  animation: push-inbox-slide 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.fade-obscurator {
  animation: push-inbox-fade 0.35s;
}

@keyframes push-inbox-slide {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes push-inbox-fade {
  from {
    background: rgba(0, 0, 0, 0);
  }
  to {
    background: rgba(0, 0, 0, 0.5);
  }
}
</style>
